import type { ChangeEvent, Ref } from 'react';
import { forwardRef, useState } from 'react';
import { cn as classNames } from '@library/component-utils/cn';

export interface InputProps extends React.ComponentPropsWithRef<'input'> {
  className?: string;
  labelClassName?: string;
  labelName?: string;
  onValueChange?: (value: string) => void | undefined;
  required?: boolean;
}

export const INPUT_DEFAULT_CLASSES =
  'bg-soFresh-110 text-soClean-100 block w-full rounded-md border border-soFresh-140 py-1.5 placeholder:text-soClean-170 text-[13px] leading-6';

const CustomInput = (
  {
    className = INPUT_DEFAULT_CLASSES,
    id,
    labelClassName = 'text-xs rounded-md uppercase leading-6 text-soClean-100',
    labelName = '',
    onChange,
    onValueChange = undefined,
    required = false,
    ...rest
  }: InputProps,
  ref: Ref<HTMLInputElement>
) => {
  const [didBlur, setDidBlur] = useState(false);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onValueChange) {
      onValueChange(event.target.value);
    }

    if (onChange) {
      onChange(event);
    }
  };

  const handleBlur = () => {
    setDidBlur(true);
  };

  return (
    <>
      {labelName && (
        <label htmlFor={id} className={labelClassName}>
          {labelName}
          {required && (
            <span
              className="text-soClean-190 pl-2 text-[10px]"
              style={{ fontVariant: 'small-caps' }}
            >
              Required
            </span>
          )}
        </label>
      )}
      <input
        id={id}
        className={classNames(
          className,
          'disabled:cursor-not-allowed',
          didBlur ? 'invalid:[&:not(:focus)]:border-roses' : ''
        )}
        onBlur={handleBlur}
        onChange={handleOnChange}
        ref={ref}
        required={required}
        {...rest}
      />
    </>
  );
};

export const Input = forwardRef(CustomInput);
